/**
 * PrefetchLink — a drop-in replacement for react-router's <Link> that
 * prefetches the target route's JS chunk (and warms the API cache) as soon
 * as the user hovers, focuses, or touches the link. By the time they
 * actually click, the chunk is already in RAM → the transition feels instant.
 *
 * We keep this dead-simple:
 *   - `import()` the route module via a caller-provided factory
 *   - dedupe via a module-level Set so a chunk is only fetched once
 *   - fall back to react-router's Link when no factory is passed
 *
 * A parallel `<GlobalLinkPrefetcher />` listens at the document level so any
 * plain <Link to="…"> nav item also gets prefetching by URL heuristics, no
 * component changes required.
 */
import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";

const _seenChunks = new Set();

function safePrefetch(factory) {
  if (!factory) return;
  const key = factory.toString().slice(0, 120);
  if (_seenChunks.has(key)) return;
  _seenChunks.add(key);
  try {
    factory().catch(() => _seenChunks.delete(key));
  } catch {
    _seenChunks.delete(key);
  }
}

export function PrefetchLink({ prefetch, ...linkProps }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!prefetch || !ref.current) return;
    const el = ref.current;
    const on = () => safePrefetch(prefetch);
    el.addEventListener("mouseenter", on, { passive: true, once: true });
    el.addEventListener("focus", on, { once: true });
    el.addEventListener("touchstart", on, { passive: true, once: true });
    return () => {
      el.removeEventListener("mouseenter", on);
      el.removeEventListener("focus", on);
      el.removeEventListener("touchstart", on);
    };
  }, [prefetch]);
  return <Link ref={ref} {...linkProps} />;
}

/**
 * URL → chunk factory map. Kept in one place so PrefetchLink AND the global
 * listener can share the same registry. Adding a new lazy route to App.js
 * only needs a new entry here for prefetching to work.
 */
const ROUTE_CHUNKS = {
  "/schedule": () => import("@/pages/Schedule"),
  "/roster": () => import("@/pages/Roster"),
  "/stats": () => import("@/pages/Stats"),
  "/news": () => import("@/pages/News"),
  "/community": () => import("@/pages/Community"),
  "/about": () => import("@/pages/About"),
  "/history": () => import("@/pages/History"),
  "/film-room": () => import("@/pages/FilmRoom"),
  "/compare": () => import("@/pages/ComparePage"),
  "/command-center": () => import("@/pages/CommandCenter"),
  "/breaking-news": () => import("@/pages/CategoryNews"),
  "/analysis": () => import("@/pages/CategoryNews"),
  "/offseason": () => import("@/pages/CategoryNews"),
};

function chunkForHref(href) {
  if (!href) return null;
  // Absolute → strip origin
  try {
    if (href.startsWith("http")) {
      const u = new URL(href);
      href = u.pathname;
    }
  } catch {
    /* noop */
  }
  const clean = href.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  if (ROUTE_CHUNKS[clean]) return ROUTE_CHUNKS[clean];
  if (clean.startsWith("/news/")) return () => import("@/pages/NewsDetail");
  if (clean.startsWith("/player/")) return () => import("@/pages/PlayerPage");
  if (clean.startsWith("/author/")) return () => import("@/pages/AuthorPage");
  if (clean.startsWith("/film-room/")) return () => import("@/pages/FilmRoomDetail");
  if (clean.startsWith("/live/")) return () => import("@/pages/LiveArticle");
  return null;
}

/**
 * GlobalLinkPrefetcher — mount once at the app root. Uses a single delegated
 * pointerover/focus listener at document level, so every <Link> on the page
 * benefits without any changes to individual components.
 *
 * Also uses IntersectionObserver to prefetch chunks for links that are
 * visible in the viewport but idle — that way the moment a user scrolls to
 * a card, its target chunk is already fetching.
 */
export default function GlobalLinkPrefetcher() {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const handlePointer = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      const factory = chunkForHref(href);
      if (factory) safePrefetch(factory);
    };
    // pointerover fires on hover (desktop) + first touchmove (mobile).
    document.addEventListener("pointerover", handlePointer, { passive: true });
    document.addEventListener("focusin", handlePointer, { passive: true });

    // Viewport-visible link prefetch (very throttled — only during idle).
    let io = null;
    const startIdlePrefetch = () => {
      if (typeof IntersectionObserver === "undefined") return;
      io = new IntersectionObserver(
        (entries) => {
          for (const en of entries) {
            if (!en.isIntersecting) continue;
            const a = en.target;
            const href = a.getAttribute?.("href");
            const factory = chunkForHref(href);
            if (factory) safePrefetch(factory);
            io.unobserve(a);
          }
        },
        { rootMargin: "300px 0px" },
      );
      document.querySelectorAll("a[href]").forEach((a) => io.observe(a));
    };
    const ric = typeof window !== "undefined" && window.requestIdleCallback;
    if (ric) ric(startIdlePrefetch, { timeout: 3000 });
    else setTimeout(startIdlePrefetch, 1500);

    return () => {
      document.removeEventListener("pointerover", handlePointer);
      document.removeEventListener("focusin", handlePointer);
      if (io) io.disconnect();
    };
  }, []);
  return null;
}
